/**
 * What day it is, for a business that trades past midnight.
 *
 * WHY NOT toISOString()
 * ---------------------
 * `new Date().toISOString().slice(0, 10)` is the UTC date, and Pakistan is
 * five hours ahead of UTC. Between midnight and 5am local, UTC is still
 * yesterday — so for the first five hours of every day:
 *
 *   - the dashboard shows yesterday's takings
 *   - the orders list opens on yesterday
 *   - journal entries are booked to the previous day, which quietly moves
 *     revenue between two days' accounts
 *
 * A restaurant is open during exactly those hours. The last of the three is
 * the one that survives into an audit.
 *
 * These use the machine's own timezone, which for a branch computer is the
 * timezone of the branch. That is the right answer for a till and the wrong
 * answer for a server in another country — so a deployed cloud must be set to
 * the business's timezone, which is what infra/docker-compose.yml does.
 */

/**
 * A date as YYYY-MM-DD, in local time.
 *
 * @param {Date} [date]  defaults to now
 */
export function dateKey(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today, locally. */
export function today() {
  return dateKey();
}

/**
 * A date some number of days from today, locally.
 *
 * Built by moving the calendar date rather than subtracting 24 hours of
 * milliseconds — those differ across a daylight-saving change, and produce a
 * repeated or skipped day exactly once a year.
 */
export function daysFromToday(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return dateKey(date);
}

/** Yesterday, locally. */
export function yesterday() {
  return daysFromToday(-1);
}

/**
 * A date some number of days from a given YYYY-MM-DD, along the calendar.
 *
 * Used to move a business day forward — closing "12 Sept" offers "13 Sept" as
 * the next day. Built on the date parts rather than millisecond arithmetic so
 * a daylight-saving change cannot repeat or skip a day, the same reason
 * daysFromToday is.
 *
 * @param {string} dateStr  a date as YYYY-MM-DD
 * @param {number} days
 */
export function addDays(dateStr, days) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return dateKey(date);
}

/**
 * Read a timestamp the server stored.
 *
 * The database writes "YYYY-MM-DD HH:MM:SS" in UTC. Two things go wrong if
 * that is handed straight to `new Date()`:
 *
 *   It is not valid ISO 8601 — that wants a T between the date and the time.
 *   V8 accepts it anyway; Safari refuses it and returns an Invalid Date, so an
 *   iPad till prints "Invalid Date" on every bill.
 *
 *   And without a Z it is read as local time. On a machine five hours ahead of
 *   UTC, an order taken twenty minutes ago reads as several hours in the
 *   future, and the time on a receipt is wrong by the offset.
 *
 * @returns {Date|null} null for anything that cannot be read
 */
export function parseServerTime(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  // PostgreSQL writes an offset as bare hours — "10:40:12.967727+00" — which no
  // browser reads as a date. Spelled out in full it is ordinary ISO. Without
  // this the shift report printed the raw database text as its opening time.
  const text = String(value)
    .trim()
    .replace(/(\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)([+-]\d{2})$/, '$1$2:00');

  // Already carries a zone or an offset — leave it alone.
  const stamped = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(text);
  const iso = text.replace(' ', 'T');

  const date = new Date(stamped ? iso : `${iso}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A timestamp as a person in the business would read it.
 * Returns the original text for anything unreadable — a raw timestamp on a
 * bill is ugly, "Invalid Date" is a fault report handed to a customer.
 */
export function formatServerTime(value, options = {}) {
  const date = parseServerTime(value);
  if (!date) return value ? String(value) : '';

  return date.toLocaleString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/** Just the time of day, for a list already filtered to one day. */
export function formatServerTimeOnly(value) {
  const date = parseServerTime(value);
  if (!date) return value ? String(value) : '';

  return date.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
}

/** How many whole minutes ago something happened. */
export function minutesSince(value, now = Date.now()) {
  const date = parseServerTime(value);
  if (!date) return 0;

  return Math.max(0, Math.floor((now - date.getTime()) / 60_000));
}
